import { useState } from 'react';
import clsx from 'clsx';
import { safeHttpUrl } from '@/lib/safeUrl';
import type { LinkPreview } from '@flock-types/chat.types';
import XEmbedCard from './XEmbedCard';
import YouTubeEmbedCard from './YouTubeEmbedCard';

const FACEBOOK_DISPLAY_HOSTS = [
  'facebook.com',
  'm.facebook.com',
  'mobile.facebook.com',
  'web.facebook.com',
  'fb.watch',
  'fb.me',
];

function isFacebookHostishText(value?: string): boolean {
  if (!value) return true;
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
  return FACEBOOK_DISPLAY_HOSTS.some(
    (host) => normalized === host || normalized.startsWith(`${host}/`)
  );
}

function displayHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

interface Props {
  preview: LinkPreview;
  own: boolean;
}

/**
 * Link preview router — web port of mobile's LinkPreviewCard:
 * YouTube → lite player card, X → real widget embed, TikTok → player iframe,
 * Facebook/generic → image-or-favicon card. Raw URL text is stripped from the
 * bubble by the caller (stripPreviewUrlFromText), exactly like mobile.
 */
export default function LinkPreviewCard({ preview, own }: Props) {
  const [imageFailed, setImageFailed] = useState(false);

  if (preview.provider === 'youtube' && preview.embedType === 'youtube-video') {
    return <YouTubeEmbedCard preview={preview} own={own} />;
  }

  if (preview.provider === 'x' && preview.embedType === 'x-post' && preview.providerId) {
    return <XEmbedCard preview={preview} own={own} />;
  }

  if (preview.provider === 'tiktok' && preview.embedType === 'tiktok-video' && preview.providerId) {
    return (
      <div
        className={clsx(
          'mt-2 w-72 max-w-full overflow-hidden rounded-xl border bg-black',
          own ? 'border-white/20' : 'border-flock-navy-200'
        )}
      >
        <iframe
          src={`https://www.tiktok.com/embed/v2/${preview.providerId}`}
          title="TikTok video"
          allow="encrypted-media; picture-in-picture; fullscreen"
          className="h-[500px] w-full border-0"
        />
      </div>
    );
  }

  const isFacebook = preview.provider === 'facebook';
  const safeImageUrl = safeHttpUrl(preview.image);
  const safeFaviconUrl = safeHttpUrl(preview.favicon);
  const hasImage = Boolean(safeImageUrl && !imageFailed);
  const title =
    isFacebook && isFacebookHostishText(preview.title)
      ? 'Facebook'
      : preview.title || preview.siteName || displayHost(preview.url);
  const domain = isFacebook
    ? 'facebook.com'
    : preview.siteName || preview.authorName || displayHost(preview.url);
  const footerText = isFacebook ? 'Open on Facebook' : domain;
  const openUrl = safeHttpUrl(preview.canonicalUrl) || safeHttpUrl(preview.url);
  const Wrapper = openUrl ? 'a' : 'div';
  const wrapperProps = openUrl
    ? { href: openUrl, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={clsx(
        'mt-2 block w-80 max-w-full overflow-hidden rounded-xl border transition',
        own
          ? 'border-white/20 bg-white/10 hover:bg-white/20'
          : 'border-flock-navy-200 bg-flock-navy-50 hover:bg-flock-navy-100'
      )}
    >
      {safeImageUrl && !imageFailed && (
        <img
          src={safeImageUrl}
          alt=""
          onError={() => setImageFailed(true)}
          className="max-h-36 w-full object-cover"
        />
      )}
      <div className="p-3">
        {!hasImage && (
          <div className="mb-1.5 flex items-center gap-2">
            {isFacebook ? (
              <span
                className="flex h-5 w-5 items-center justify-center rounded-full text-white"
                style={{ backgroundColor: '#1877F2' }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
                  <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" />
                </svg>
              </span>
            ) : safeFaviconUrl ? (
              <img src={safeFaviconUrl} alt="" className="h-4 w-4 rounded-sm" />
            ) : null}
            <span className={clsx('text-xs', own ? 'text-white/70' : 'text-flock-navy-400')}>
              {domain}
            </span>
          </div>
        )}
        <div
          className={clsx(
            'line-clamp-2 text-sm font-semibold',
            own ? 'text-white' : 'text-flock-navy-900'
          )}
        >
          {title}
        </div>
        {preview.description && (
          <div className={clsx('mt-0.5 line-clamp-2 text-xs', own ? 'text-white/70' : 'text-flock-navy-500')}>
            {preview.description}
          </div>
        )}
        <div className={clsx('mt-1 text-xs', own ? 'text-white/60' : 'text-flock-navy-400')}>
          {footerText}
        </div>
      </div>
    </Wrapper>
  );
}
